import { useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList, ReferenceLine, Customized } from 'recharts'
import useTranslation from '../../../../hooks/useTranslation'
import AnalyticsCard from './AnalyticsCard'
import { Headline, KpiRow } from './Kpi'
import { ChartBox, Legend, TooltipCard, axisProps } from './ChartParts'
import { rankGroups } from './measures'
import useAnalyticsWidget from './useAnalyticsWidget'
import useElementSize from './useElementSize'
import { chartFont } from '../../widgetTypography'

/*
 * How a starting figure becomes an ending one: a full bar for the start, a
 * floating bar for each step up or down, a full bar for where it ends.
 *
 *     waterfall: {
 *         format: 'currency',
 *         start: { label, value: <expression> },
 *         steps: [
 *             { label, value: <expression>, sign: -1 },        one step; `sign` turns a
 *                                                               cost measure into a fall
 *             { group: 'spend', limit: 3, sign: -1, otherLabel } one step per category of
 *                                                               a grouped measure, the
 *                                                               largest first, the rest as one
 *         ],
 *         end: { label }
 *     }
 *
 * The end is not read — it is the start plus every step, so the bridge always
 * reconciles. The running total may go below zero and back; each step is drawn
 * from where the last one ended, and a thin line carries that level across.
 */
const WaterfallWidget = ({ type, data: config, labels }) => {
    const { t } = useTranslation()
    const w = useAnalyticsWidget(type, config)
    const { period, data, palette, format } = w
    const [ref, size] = useElementSize()
    const wf = config.waterfall ?? {}
    const valueFormat = wf.format ?? 'currency'

    const colors = { total: palette.color('primary'), up: palette.tokens.success, down: palette.tokens.danger }

    const rows = useMemo(() => {
        if (!data.current) return []
        const cur = data.current
        const out = []
        let running = cur.total(wf.start?.value) ?? 0
        out.push({ id: 'start', name: t(wf.start?.label), kind: 'total', range: [0, running], amount: running, end: running })

        for (const [i, step] of (wf.steps ?? []).entries()) {
            const sign = step.sign ?? 1
            const parts = step.group
                ? rankGroups(cur.groups(step.group), { limit: step.limit, other: true })
                    .filter(g => g.value)
                    .map(g => ({ id: `${i}-${g.key}`, name: g.other ? t(step.otherLabel ?? 'analytics.other') : g.name ?? (g.key || t('analytics.uncategorised')), amount: g.value * sign }))
                : [{ id: `${i}`, name: t(step.label), amount: (cur.total(step.value) ?? 0) * sign }]
            for (const p of parts) {
                const from = running
                running += p.amount
                out.push({ ...p, kind: p.amount >= 0 ? 'up' : 'down', range: [from, running], end: running })
            }
        }
        out.push({ id: 'end', name: t(wf.end?.label), kind: 'total', range: [0, running], amount: running, end: running })
        return out
    }, [data.current, wf, t])

    const compact = size.height < 140 || size.width < 260
    const showYAxis = !compact && size.width >= 380
    const barWidth = rows.length ? size.width / rows.length : 0
    const showLabels = !compact && barWidth >= 44
    const total = rows[0]?.amount

    // The level each bar ends at, drawn across to the next bar
    // — read off the axis scale, since a falling bar's own geometry may come
    // back with a negative height depending on how recharts normalised it
    const Connectors = ({ formattedGraphicalItems, yAxisMap }) => {
        const bars = formattedGraphicalItems?.[0]?.props?.data ?? []
        const scale = Object.values(yAxisMap ?? {})[0]?.scale
        if (!scale) return null
        return (
            <g>
                {bars.slice(0, -1).map((bar, i) => {
                    const next = bars[i + 1]
                    if (!next || !rows[i]) return null
                    const y = scale(rows[i].end)
                    return <line key={i} x1={bar.x + bar.width} x2={next.x} y1={y} y2={y}
                        stroke={palette.axis} strokeWidth={1} strokeDasharray="3 2" />
                })}
            </g>
        )
    }

    const renderTooltip = ({ active, payload }) => {
        if (!active || !payload?.length) return null
        const row = payload[0].payload
        const items = row.kind === 'total'
            ? [{ color: colors.total, name: t('analytics.waterfall.total'), value: format(row.amount, valueFormat) }]
            : [
                { color: colors[row.kind], name: t('analytics.waterfall.change'), value: format(row.amount, valueFormat, { signed: true }) },
                { name: t('analytics.waterfall.running'), value: format(row.end, valueFormat) },
            ]
        if (row.kind !== 'total' && total) items.push({ name: t('analytics.waterfall.ofStart'), value: format((row.amount / Math.abs(total)) * 100, 'percent', { signed: true }) })
        return <TooltipCard title={row.name} rows={items} />
    }

    const legend = !compact && (
        <Legend items={[
            { id: 'up', name: t('analytics.waterfall.increase'), color: colors.up },
            { id: 'down', name: t('analytics.waterfall.decrease'), color: colors.down },
            { id: 'total', name: t('analytics.waterfall.total'), color: colors.total },
        ]} />
    )

    // Long category names would collide under narrow bars
    const maxChars = Math.max(4, Math.floor(barWidth / 7))
    const tickFormatter = (name = '') => name.length > maxChars ? `${name.slice(0, maxChars - 1)}…` : name

    return (
        <AnalyticsCard
            labels={labels}
            period={period}
            status={w.status}
            error={data.error}
            onRetry={data.refetch}
            headline={<Headline kpi={w.headline} formatValue={format} compareLabel={w.compareLabel} />}
            footer={<KpiRow kpis={w.kpis} formatValue={format} />}
        >
            {legend}
            <ChartBox>
                <div ref={ref}>
                    {size.width > 0 && size.height > 0 && (
                        <BarChart width={size.width} height={size.height} data={rows} accessibilityLayer barCategoryGap="22%"
                            margin={{ top: showLabels ? 18 : 6, right: 4, left: showYAxis ? 0 : 4, bottom: 0 }}>
                            {!compact && <CartesianGrid vertical={false} stroke={palette.grid} strokeDasharray="3 3" />}
                            <XAxis dataKey="name" {...axisProps(palette)} interval={0} tickFormatter={tickFormatter} />
                            <YAxis hide={!showYAxis} width={52} {...axisProps(palette)} domain={['auto', 'auto']}
                                tickFormatter={v => format(v, valueFormat, { compact: true })} />
                            <ReferenceLine y={0} stroke={palette.grid} />
                            <Tooltip content={renderTooltip} cursor={{ fill: palette.track }} />
                            <Bar dataKey="range" isAnimationActive={false} radius={3} maxBarSize={64}>
                                {rows.map(row => <Cell key={row.id} fill={colors[row.kind]} />)}
                                {showLabels && (
                                    <LabelList dataKey="amount" position="top" fontSize={chartFont.label} fill={palette.axis}
                                        formatter={v => format(v, valueFormat, { compact: true })} />
                                )}
                            </Bar>
                            <Customized component={Connectors} />
                        </BarChart>
                    )}
                </div>
            </ChartBox>
        </AnalyticsCard>
    )
}

export default WaterfallWidget
